import { useState } from "react";
import axios from "axios";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { toast } from "sonner";
import { CheckCircle2, Loader2, Users } from "lucide-react";

const API = process.env.REACT_APP_BACKEND_URL;

export default function WaitlistForm({ price }) {
  const [form, setForm] = useState({ dealership_name: "", admin_name: "", admin_email: "", dealership_phone: "", dealership_state: "" });
  const [busy, setBusy] = useState(false);
  const [joined, setJoined] = useState(false);

  const onSubmit = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      await axios.post(`${API}/api/billing/waitlist`, {
        dealership_name: form.dealership_name.trim(),
        admin_email: form.admin_email.trim().toLowerCase(),
        admin_name: form.admin_name.trim() || null,
        dealership_phone: form.dealership_phone.trim() || null,
        dealership_state: form.dealership_state.trim() || null,
      });
      setJoined(true);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Couldn't join waitlist");
    } finally { setBusy(false); }
  };

  if (joined) {
    return (
      <div className="max-w-md space-y-5" data-testid="waitlist-joined">
        <div className="w-16 h-16 rounded-full bg-emerald-500/10 flex items-center justify-center">
          <CheckCircle2 className="w-10 h-10 text-emerald-600" />
        </div>
        <div>
          <h2 className="font-display text-3xl font-bold tracking-tight text-navy">You're on the list</h2>
          <div className="gold-divider my-4 max-w-[80px]" />
          <p className="text-sm text-muted-foreground">
            When a founding-dealer seat opens up — or when we open the next tier — you'll be the first to know. We'll email <span className="font-mono text-navy">{form.admin_email}</span>.
          </p>
        </div>
      </div>
    );
  }

  return (
    <form onSubmit={onSubmit} className="max-w-md space-y-5" data-testid="waitlist-form">
      <div className="flex items-center gap-3">
        <div className="p-1 bg-red-600 rounded-md shadow-md">
          <Users className="w-6 h-6 text-white" />
        </div>
        <div>
          <div className="font-display font-black text-xl leading-none text-navy">Founding seats are full</div>
          <div className="font-mono-label text-red-700 text-[0.6rem] mt-1">SOLD OUT · WAITLIST ONLY</div>
        </div>
      </div>

      <div>
        <h1 className="font-display text-4xl font-bold tracking-tighter">Join the waitlist</h1>
        <p className="text-sm text-muted-foreground mt-2">
          {price ? <>The {price} founding-dealer rate is locked, </>: null}but the seats are gone.
          Drop your info and we'll reach out the moment one frees up — or when we open the next tier.
        </p>
      </div>

      <div className="grid grid-cols-1 gap-3">
        <div>
          <Label htmlFor="w_dealership" className="font-mono-label">Dealership name</Label>
          <Input id="w_dealership" required value={form.dealership_name} onChange={(e) => setForm({ ...form, dealership_name: e.target.value })} placeholder="Molle AutoGroup" className="mt-1.5" data-testid="waitlist-dealership" />
        </div>
        <div>
          <Label htmlFor="w_name" className="font-mono-label">Your name</Label>
          <Input id="w_name" value={form.admin_name} onChange={(e) => setForm({ ...form, admin_name: e.target.value })} className="mt-1.5" data-testid="waitlist-name" />
        </div>
        <div>
          <Label htmlFor="w_email" className="font-mono-label">Work email</Label>
          <Input id="w_email" type="email" required value={form.admin_email} onChange={(e) => setForm({ ...form, admin_email: e.target.value })} placeholder="you@dealership.com" className="mt-1.5" data-testid="waitlist-email" />
        </div>
        <div className="grid grid-cols-2 gap-3">
          <div>
            <Label htmlFor="w_phone" className="font-mono-label">Phone</Label>
            <Input id="w_phone" value={form.dealership_phone} onChange={(e) => setForm({ ...form, dealership_phone: e.target.value })} className="mt-1.5" data-testid="waitlist-phone" />
          </div>
          <div>
            <Label htmlFor="w_state" className="font-mono-label">State</Label>
            <Input id="w_state" value={form.dealership_state} onChange={(e) => setForm({ ...form, dealership_state: e.target.value })} className="mt-1.5" placeholder="KS" maxLength={2} data-testid="waitlist-state" />
          </div>
        </div>
      </div>

      <Button type="submit" disabled={busy} className="w-full" data-testid="waitlist-submit-btn">
        {busy ? (<><Loader2 className="w-4 h-4 mr-2 animate-spin" /> Adding you to the list…</>) : "Save my spot"}
      </Button>
    </form>
  );
}
